'use client';

import { AppButton } from '@/components/design/forms';
import type { ReactNode } from 'react';
import { useMemo, useState } from 'react';
import { useTranslations } from 'next-intl';
import { FileText, Check } from 'lucide-react';
import {
  InternationalClauseBody,
  LiabilityWaiverBody,
  PaymentRefundBody,
  PrivacyPolicyBody,
  PublicOfferBody,
  RegistrationRulesBody,
  TermsOfUseBody,
  ChampionshipRulesBody,
  FestivalChampionshipRulesBody,
} from '@/components/design/shared/LegalDocumentBodies';

type DocumentId =
  | 'festival-specific'
  | 'registration-rules'
  | 'public-offer'
  | 'terms-of-use'
  | 'payment-refund'
  | 'privacy-policy'
  | 'liability-waiver'
  | 'championship-rules'
  | 'international-clause'
  | 'festival-rules-championship';

interface Document {
  id: DocumentId;
  title: string;
  content: ReactNode;
}

interface DocumentGroup {
  label: string;
  docs: Document[];
}

interface LegalDocumentsBlockProps {
  festivalName?: string;
  festivalSpecificRules?: ReactNode;
}

export function LegalDocumentsBlock({ festivalName, festivalSpecificRules }: LegalDocumentsBlockProps) {
  const t = useTranslations('legalDocs');
  const [activeDoc, setActiveDoc] = useState<DocumentId>(
    festivalSpecificRules ? 'festival-specific' : 'registration-rules',
  );

  const groups = useMemo((): DocumentGroup[] => {
    const bakuDocs: Document[] = [];

    if (festivalSpecificRules && festivalName) {
      bakuDocs.push({
        id: 'festival-specific',
        title: t('nav.festival-specific', { festivalName }),
        content: festivalSpecificRules,
      });
    }

    bakuDocs.push(
      {
        id: 'registration-rules',
        title: t('nav.registration-rules'),
        content: <RegistrationRulesBody />,
      },
      {
        id: 'public-offer',
        title: t('nav.public-offer'),
        content: <PublicOfferBody />,
      },
      {
        id: 'terms-of-use',
        title: t('nav.terms-of-use'),
        content: <TermsOfUseBody />,
      },
      {
        id: 'payment-refund',
        title: t('nav.payment-refund'),
        content: <PaymentRefundBody />,
      },
      {
        id: 'privacy-policy',
        title: t('nav.privacy-policy'),
        content: <PrivacyPolicyBody />,
      },
      {
        id: 'liability-waiver',
        title: t('nav.liability-waiver'),
        content: <LiabilityWaiverBody />,
      },
      {
        id: 'championship-rules',
        title: t('nav.championship-rules'),
        content: <ChampionshipRulesBody />,
      },
    );

    return [
      { label: t('groupBaku'), docs: bakuDocs },
      {
        label: t('groupMetro'),
        docs: [
          {
            id: 'international-clause',
            title: t('nav.international-clause'),
            content: <InternationalClauseBody />,
          },
          {
            id: 'festival-rules-championship',
            title: t('nav.festival-rules-championship'),
            content: <FestivalChampionshipRulesBody />,
          },
        ],
      },
    ];
  }, [festivalName, festivalSpecificRules, t]);

  const activeDocument = groups
    .flatMap((g) => g.docs)
    .find((doc) => doc.id === activeDoc);

  return (
    <div className="flex flex-col gap-8 lg:flex-row lg:items-start">
      <div className="w-full flex-shrink-0 lg:w-80">
        <div className="lg:sticky lg:top-24 bg-white border-2 border-fest-border rounded-2xl p-4 shadow-md">
          <h3 className="text-lg font-bold text-fest-ink mb-4 px-2">{t('sidebarHeading')}</h3>
          <nav className="space-y-4">
            {groups.map((group) => (
              <div key={group.label}>
                <p className="px-2 mb-1 text-xs font-semibold uppercase tracking-widest text-fest-silver">
                  {group.label}
                </p>
                <div className="space-y-1">
                  {group.docs.map((doc) => (
                    <AppButton
                      type="button"
                      key={doc.id}
                      onClick={() => setActiveDoc(doc.id)}
                      className={`w-full text-left px-4 py-3 rounded-lg transition-all flex items-center gap-3 ${
                        activeDoc === doc.id
                          ? 'bg-fest-gold text-black font-medium shadow-md'
                          : 'text-fest-muted-text hover:bg-fest-surface hover:text-fest-ink'
                      }`}
                    >
                      {activeDoc === doc.id ? (
                        <Check className="w-4 h-4 flex-shrink-0" />
                      ) : (
                        <FileText className="w-4 h-4 flex-shrink-0" />
                      )}
                      <div className="flex-1 text-sm">
                        <div className={activeDoc === doc.id ? 'font-bold' : 'font-medium'}>{doc.title}</div>
                      </div>
                    </AppButton>
                  ))}
                </div>
              </div>
            ))}
          </nav>
        </div>
      </div>

      <div className="min-w-0 flex-1">
        <div className="bg-white border-2 border-fest-border rounded-2xl p-8 md:p-12 shadow-md">
          {activeDocument?.content}

          <div className="mt-12 flex flex-col gap-4 border-t border-fest-border pt-8 sm:flex-row sm:items-center sm:justify-between">
            <div className="text-sm text-fest-silver">{t('lastUpdated')}</div>
            <AppButton
              type="button"
              onClick={() => window.print()}
              className="px-6 py-2 bg-fest-ink text-white rounded-lg hover:bg-fest-void transition-colors text-sm font-medium"
            >
              {t('print')}
            </AppButton>
          </div>
        </div>
      </div>
    </div>
  );
}
